// @flow
import capitalize from 'lodash/capitalize';

import type { DecoratedProduct } from '@gandi/cart';
import type { ApplicationSummary } from '@gandi/redux-resource-simplehosting';
import { useTranslator } from '@gandi/translate';

import type { GandiFormSelectOption } from 'src/Domain/types';
import { useHostingApplications } from 'src/WebHosting/modules/hooks';
import {
  usePlanPrices,
  useSimplehostingPrices,
  type HostingPlan,
} from 'src/WebHosting/modules/prices';

import type {
  InstanceSize,
  SimplehostingPriceSummary,
  PricesResourceType,
} from 'src/modules/simplehosting/prices';

export type PaasUpdateSubProcess = 'resize' | 'add_disk' | 'unknown';

export function useAvailableSizes(
  plan: HostingPlan,
  applicationName?: ?string,
): void | Array<GandiFormSelectOption<InstanceSize>> {
  const { availableApplications, isFetching: isFetchingApplications } =
    useHostingApplications();

  const application: ?ApplicationSummary = applicationName
    ? availableApplications.find(app => app.name === applicationName)
    : undefined;

  const planPrices = usePlanPrices(plan, application);

  if (!planPrices || (applicationName && isFetchingApplications))
    return undefined;

  return planPrices
    .map(price => {
      if (price.product.name !== 'essential' || plan === 'yearly') {
        const name = price.product.name;
        return { value: name, text: capitalize(name) };
      }
      return null;
    })
    .filter(Boolean);
}

export const getPriceBeforeDiscount = ({
  product,
  isBusy = false,
}: {
  product: DecoratedProduct,
  /** pass to `isBuzy` a boolean that represent its business but `product.isWorking` */
  isBusy?: boolean,
}): number | void => {
  if (
    product.isWorking ||
    isBusy ||
    // product.total?.before_taxes === 0 when there is a lang error
    product.total?.before_taxes === 0
  ) {
    return undefined;
  }
  if (
    !!product.discounts?.length &&
    product.total?.before_discount !== undefined
  ) {
    return Number(product.total.before_discount);
  }
  return product.total?.before_taxes;
};

export const getPrice = ({
  product,
  isBusy = false,
}: {
  product: DecoratedProduct,
  /** pass to `isBusy` a boolean that represent its business but not `product.isWorking` */
  isBusy?: boolean,
}): number | void =>
  product.isWorking || isBusy
    ? undefined
    : // product.total?.before_taxes === 0 when there is a lang error
      product.total?.before_taxes;

type ConfigurationsDetails = {
  duration: number,
  size: InstanceSize,
  process: DecoratedProduct['process'],
};

function useConfigurationDetailsCommon({
  duration,
  size,
  process,
}: ConfigurationsDetails): {
  cpu: number,
  GB: string,
  instancePrice: SimplehostingPriceSummary,
  MB: string,
  ram: number,
  storage: number,
  TB: string,
} | void {
  const { summaries: instancesPrices, isLoaded: instancesPricesLoaded } =
    useSimplehostingPrices(process);

  const { __ } = useTranslator();

  if (!instancesPricesLoaded) return undefined;

  const instancePrice = instancesPrices?.find(
    price => price.min_duration === duration && price.product.name === size,
  );

  if (!instancePrice) return undefined;

  const { product } = instancePrice;
  const { params } = product;
  const { cpu, storage, ram } = params;

  if (!cpu || !ram || !storage) {
    return undefined;
  }

  const GB = __('<for-translators-use>gigabytes</for-translators-use>GB');
  const TB = __('<for-translators-use>terabytes</for-translators-use>TB');
  const MB = __('<for-translators-use>megabytes</for-translators-use>MB');

  return {
    cpu,
    instancePrice,
    ram,
    storage,
    GB,
    MB,
    TB,
  };
}

export function useConfigurationDetails({
  duration,
  size,
  process,
}: ConfigurationsDetails): void | string {
  const { __, formatPrice } = useTranslator();
  const configuration = useConfigurationDetailsCommon({
    duration,
    size,
    process,
  });

  if (!configuration) return undefined;

  const { cpu, instancePrice, ram, storage, GB, MB, TB } = configuration;

  return __(
    '%(price)s/month - up to %(storage)s %(storage_unit)s/%(cpu_count)s CPU/%(ram)s %(ram_unit)s RAM',
    {
      price: formatPrice(instancePrice.price, instancePrice.currency),
      storage: storage < 1000 ? storage : storage / 1000,
      storage_unit: storage < 1000 ? GB : TB,
      cpu_count: cpu,
      ram: ram < 1024 ? ram : ram / 1024,
      ram_unit: ram < 1024 ? MB : GB,
    },
  );
}

export function useConfigurationDetailsUpSell({
  duration,
  size,
  process,
}: ConfigurationsDetails): void | {
  storage: string,
  cpu: string,
  mailbox: string,
} {
  const { __ } = useTranslator();
  const configuration = useConfigurationDetailsCommon({
    duration,
    size,
    process,
  });

  if (!configuration) return undefined;

  const { cpu, ram, storage, GB, MB, TB } = configuration;

  const isEssential = size === 'essential';
  const mailboxesNB = isEssential ? 1 : 2;

  const mailbox = __('%(count)d mailboxes', {
    count: mailboxesNB,
  });

  return {
    storage: __('%(storage)s %(storage_unit)s storage', {
      storage: storage < 1000 ? storage : storage / 1000,
      storage_unit: storage < 1000 ? GB : TB,
    }),
    cpu: __('%(cpu_count)s CPU / %(ram)s %(ram_unit)s RAM', {
      cpu_count: cpu,
      ram: ram < 1024 ? ram : ram / 1024,
      ram_unit: ram < 1024 ? MB : GB,
    }),
    mailbox,
  };
}

export type Option = GandiFormSelectOption<InstanceSize>;
export type Options = Array<Option>;

type WebhostingPlanPricesResult = {
  firstPrice: string,
  firstPriceCrossedOutForWHSE: string,
  formattedDiffMonthly?: string,
  pricesByPlanByDuration: { [InstanceSize]: string },
};

export function useWebhostingPlanPrices(
  duration: number,
  plans: Options,
  process: DecoratedProduct['process'],
): WebhostingPlanPricesResult | void {
  const { __, formatPrice } = useTranslator();
  const { summaries: instancesPrices, isLoaded: instancesPricesLoaded } =
    useSimplehostingPrices(process);

  if (!instancesPricesLoaded || !plans.length) {
    return undefined;
  }

  const findPriceForPlan = (
    planValue: number | string,
    otherDuration?: number,
  ) => {
    return instancesPrices?.find(
      price =>
        price.min_duration === (otherDuration || duration) &&
        price.product.name === planValue,
    );
  };

  const firstPrice = findPriceForPlan(plans[0].value);

  /**
   * Strikethrough price used for WebHostingSizeError error
   */
  const firstPriceCrossedOutForWHSE = findPriceForPlan(plans[0].value, 1);

  if (!firstPrice) {
    return undefined;
  }

  const createPriceMapByPlanByDuration = (): { [InstanceSize]: string } => {
    return plans.reduce((acc, plan) => {
      const priceFor1Month = instancesPrices?.find(
        price => price.min_duration === 1 && price.product.name === plan.value,
      );

      const priceFor12Months = instancesPrices?.find(
        price => price.min_duration === 12 && price.product.name === plan.value,
      );

      if (priceFor1Month && priceFor12Months) {
        const annualSavings =
          (priceFor1Month.price - priceFor12Months.price) * 12;
        if (!annualSavings) return acc;
        acc[(plan.value: any)] = formatPrice(
          annualSavings,
          firstPrice.currency,
        );
      }

      return acc;
    }, {});
  };

  const pricesByPlanByDuration = createPriceMapByPlanByDuration();

  let formattedDiffMonthly: string | void;

  if (plans.length >= 2) {
    const secondPrice = findPriceForPlan(plans[1].value);

    if (secondPrice) {
      const diffMonthly = secondPrice.price - firstPrice.price;

      if (diffMonthly > 0) {
        formattedDiffMonthly = __('+%(diffMonthly)s', {
          diffMonthly: formatPrice(diffMonthly, firstPrice.currency),
        });
      }
    }
  }

  return {
    firstPrice: formatPrice(firstPrice.price, firstPrice.currency, {
      round: true,
    }),
    firstPriceCrossedOutForWHSE:
      firstPriceCrossedOutForWHSE &&
      firstPriceCrossedOutForWHSE.price > firstPrice.price
        ? formatPrice(
            firstPriceCrossedOutForWHSE.price,
            firstPriceCrossedOutForWHSE.currency,
          )
        : '0',
    formattedDiffMonthly,
    pricesByPlanByDuration,
  };
}

const findPriceByDuration =
  (duration: number, size: InstanceSize | number) =>
  (price: PricesResourceType['fetchResource']) =>
    price.min_duration === duration && price.product.name === size;

export function useWebhostingDiffPrices(
  size?: InstanceSize | number,
): string | void {
  const { __ } = useTranslator();
  const { summaries: instancesPrices, isLoaded: instancesPricesLoaded } =
    useSimplehostingPrices('create');
  if (!instancesPrices?.length || !instancesPricesLoaded || !size)
    return undefined;

  const priceFor1Month = instancesPrices.find(findPriceByDuration(1, size));
  const priceFor12Months = instancesPrices.find(findPriceByDuration(12, size));

  if (!priceFor1Month || !priceFor12Months) return undefined;

  const diff = Math.abs(
    Math.round(
      (priceFor12Months.price * 12 - priceFor1Month.price * 12) /
        priceFor1Month.price,
    ),
  );

  if (!diff) return undefined;

  const diffMonthly = __('%(count)s months free', {
    count: diff,
  });

  return diffMonthly;
}
